import { useState, useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const COUNTRY_CODES = [
  { code: "+1", country: "US", flag: "🇺🇸", label: "United States" },
  { code: "+44", country: "GB", flag: "🇬🇧", label: "United Kingdom" },
  { code: "+91", country: "IN", flag: "🇮🇳", label: "India" },
  { code: "+61", country: "AU", flag: "🇦🇺", label: "Australia" },
  { code: "+49", country: "DE", flag: "🇩🇪", label: "Germany" },
  { code: "+33", country: "FR", flag: "🇫🇷", label: "France" },
  { code: "+81", country: "JP", flag: "🇯🇵", label: "Japan" },
  { code: "+1", country: "CA", flag: "🇨🇦", label: "Canada" },
  { code: "+55", country: "BR", flag: "🇧🇷", label: "Brazil" },
  { code: "+52", country: "MX", flag: "🇲🇽", label: "Mexico" },
  { code: "+34", country: "ES", flag: "🇪🇸", label: "Spain" },
  { code: "+39", country: "IT", flag: "🇮🇹", label: "Italy" },
  { code: "+964", country: "IQ", flag: "🇮🇶", label: "Iraq" },
];

/** The number input's error state, so a form can mark it invalid and point at the reason. */
interface SimplePhoneInputProps extends Pick<React.ComponentProps<"input">, "aria-invalid" | "aria-describedby"> {
  /** E.164, e.g. `+15551234567`; empty while no digits are typed. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

export function SimplePhoneInput({ value, onChange, placeholder = "1234567890", id, className, ...inputProps }: SimplePhoneInputProps) {
  // Store full composite key (code:country) to properly distinguish US/Canada
  const [selectedCountryKey, setSelectedCountryKey] = useState("+1:US");
  const [localNumber, setLocalNumber] = useState("");

  // Initialize from value prop
  useEffect(() => {
    if (!value) {
      setLocalNumber("");
      return;
    }

    // Try to find matching country code
    const sortedCodes = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    const matchingCountry = sortedCodes.find(c => value.startsWith(c.code));

    if (matchingCountry) {
      // If current selected key matches the code, keep it (preserves US vs CA selection)
      // Otherwise, switch to the first match
      if (!selectedCountryKey.startsWith(matchingCountry.code)) {
        setSelectedCountryKey(`${matchingCountry.code}:${matchingCountry.country}`);
      }
      // Strip the prefix for the local number
      setLocalNumber(value.slice(matchingCountry.code.length));
    } else {
      setLocalNumber(value);
    }
  }, [value, selectedCountryKey]); // Add selectedCountryKey dependency to prevent flapping

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    const cleanLocal = inputVal.replace(/\D/g, ''); 
    setLocalNumber(cleanLocal);
    
    // Extract code from composite key
    const currentCode = selectedCountryKey.split(':')[0];
    // A country code alone is not a number: it would be saved and dialled as one.
    onChange(cleanLocal ? `${currentCode}${cleanLocal}` : "");
  };

  const handleCountryChange = (newKey: string) => {
    setSelectedCountryKey(newKey);
    const newCode = newKey.split(':')[0];
    onChange(localNumber ? `${newCode}${localNumber}` : "");
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <Select value={selectedCountryKey} onValueChange={handleCountryChange}>
        <SelectTrigger className="w-[110px] flex-shrink-0">
          <SelectValue placeholder="Country" />
        </SelectTrigger>
        <SelectContent side="top" className="max-h-[300px]">
          {COUNTRY_CODES.map((c) => (
            <SelectItem key={`${c.country}-${c.code}`} value={`${c.code}:${c.country}`}>
              <span className="flex items-center gap-2">
                <span className="text-lg">{c.flag}</span>
                <span className="text-muted-foreground">{c.code}</span>
              </span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        id={id}
        placeholder={placeholder}
        value={localNumber}
        onChange={handleNumberChange}
        className="flex-1"
        type="tel"
        {...inputProps}
      />
    </div>
  );
}

